import React, { useState, useEffect } from 'react';
import { Bell, Building2, Filter, Calendar, Loader2, AlertTriangle, Clock, ClipboardList, CheckCircle, XCircle, Search, ChevronDown } from 'lucide-react';
import { API } from '../../config/apiBase';


const TIPO_LABELS = {
    entrada: 'Entrada',
    saida: 'Saida',
    intervalo_inicio: 'Inicio Intervalo',
    intervalo_fim: 'Fim Intervalo',
    negado: 'Negado',
    fora_geofence: 'Fora Geofence',
    atraso: 'Atraso',
    ausencia: 'Ausencia',
    ajuste: 'Ajuste',
    presenca_baixa: 'Presenca Baixa',
    atrasos: 'Atrasos',
    ajustes_pendentes: 'Ajustes Pendentes',
    resumo: 'Resumo',
    info: 'Info',
};

const TIPO_COLORS = {
    presenca_baixa: { bg: 'rgba(239,68,68,0.08)', text: '#EF4444' },
    atrasos: { bg: 'rgba(245,158,11,0.08)', text: '#F59E0B' },
    ajustes_pendentes: { bg: 'rgba(139,92,246,0.08)', text: '#8B5CF6' },
    entrada: { bg: 'rgba(16,185,129,0.08)', text: '#10B981' },
    saida: { bg: 'rgba(59,130,246,0.08)', text: '#3B82F6' },
    fora_geofence: { bg: 'rgba(239,68,68,0.08)', text: '#EF4444' },
    negado: { bg: 'rgba(239,68,68,0.08)', text: '#EF4444' },
    atraso: { bg: 'rgba(245,158,11,0.08)', text: '#F59E0B' },
    resumo: { bg: 'rgba(59,130,246,0.08)', text: '#3B82F6' },
    info: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280' },
};

const SOURCE_LABELS = {
    ponto_event: 'Evento de Ponto',
    unit_alert: 'Alerta de Unidade',
    unit_alert_test: 'Teste de Alerta',
};

export default function EmpresaAlertasHistoricoPage() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [unidades, setUnidades] = useState([]);
    const [filters, setFilters] = useState({ unidade_id: '', tipo: '', source: '', start_date: '', end_date: '' });
    const [showFilters, setShowFilters] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    const loadAlerts = async (f = filters) => {
        setLoading(true);
        const params = new URLSearchParams({ limit: '100' });
        if (f.unidade_id) params.set('unidade_id', f.unidade_id);
        if (f.tipo) params.set('tipo', f.tipo);
        if (f.source) params.set('source', f.source);
        if (f.start_date) params.set('start_date', f.start_date);
        if (f.end_date) params.set('end_date', f.end_date);
        try {
            const r = await fetch(`${API}/ponto/telegram-alerts/history?${params}`, { headers });
            const data = await r.json();
            setAlerts(Array.isArray(data) ? data : []);
        } catch { setAlerts([]); }
        setLoading(false);
    };

    useEffect(() => {
        loadAlerts();
        fetch(`${API}/ponto/unidades`, { headers })
            .then(r => r.ok ? r.json() : []).then(u => setUnidades(Array.isArray(u) ? u : [])).catch(() => {});
    }, []);

    const handleFilter = () => loadAlerts(filters);

    const clearFilters = () => {
        const empty = { unidade_id: '', tipo: '', source: '', start_date: '', end_date: '' };
        setFilters(empty);
        loadAlerts(empty);
    };

    const hasActiveFilters = Object.values(filters).some(v => v);

    // Group by date
    const grouped = alerts.reduce((acc, a) => {
        const date = (a.timestamp || '').split('T')[0] || 'Sem data';
        if (!acc[date]) acc[date] = [];
        acc[date].push(a);
        return acc;
    }, {});

    // Stats
    const totalAlerts = alerts.length;
    const bySource = alerts.reduce((acc, a) => { acc[a.source || 'unknown'] = (acc[a.source || 'unknown'] || 0) + 1; return acc; }, {});
    const byTipo = alerts.reduce((acc, a) => { acc[a.tipo || 'unknown'] = (acc[a.tipo || 'unknown'] || 0) + 1; return acc; }, {});

    return (
        <div className="space-y-5" data-testid="alertas-historico-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Historico de Alertas</h1>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>Acompanhe todos os alertas gerados para sua empresa</p>
                </div>
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all" style={{ background: hasActiveFilters ? 'rgba(var(--sv-accent-rgb), 0.12)' : 'var(--surface)', border: `1px solid ${hasActiveFilters ? 'rgba(var(--sv-accent-rgb), 0.3)' : 'var(--border)'}`, color: hasActiveFilters ? 'var(--sv-gold-primary)' : 'var(--muted)' }} data-testid="toggle-filters-btn">
                    <Filter size={13} /> Filtros {hasActiveFilters && <span className="w-2 h-2 rounded-full" style={{ background: 'var(--sv-gold-primary)' }} />}
                </button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="rounded-xl p-4 space-y-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="filters-panel">
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Unidade</label>
                            <select value={filters.unidade_id} onChange={e => setFilters({ ...filters, unidade_id: e.target.value })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-unidade">
                                <option value="">Todas</option>
                                {unidades.map(u => <option key={u.unidade_id} value={u.unidade_id}>{u.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Tipo</label>
                            <select value={filters.tipo} onChange={e => setFilters({ ...filters, tipo: e.target.value })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-tipo">
                                <option value="">Todos</option>
                                {Object.entries(TIPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Origem</label>
                            <select value={filters.source} onChange={e => setFilters({ ...filters, source: e.target.value })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-source">
                                <option value="">Todas</option>
                                {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Data inicio</label>
                            <input type="date" value={filters.start_date} onChange={e => setFilters({ ...filters, start_date: e.target.value })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-start-date" />
                        </div>
                        <div>
                            <label className="text-[10px] block mb-1" style={{ color: 'var(--muted-2)' }}>Data fim</label>
                            <input type="date" value={filters.end_date} onChange={e => setFilters({ ...filters, end_date: e.target.value })} className="w-full rounded-lg px-2 py-1.5 text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="filter-end-date" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleFilter} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="apply-filters-btn">
                            <Search size={12} /> Aplicar Filtros
                        </button>
                        {hasActiveFilters && (
                            <button onClick={clearFilters} className="px-3 py-2 rounded-lg text-xs" style={{ color: 'var(--muted)', border: '1px solid var(--border)' }} data-testid="clear-filters-btn">
                                Limpar
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Bell size={14} style={{ color: 'var(--sv-gold-primary)' }} />
                        <span className="text-[10px] sm:text-xs" style={{ color: 'var(--muted)' }}>Total de Alertas</span>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }} data-testid="total-alerts-count">{totalAlerts}</p>
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={14} style={{ color: '#EF4444' }} />
                        <span className="text-[10px] sm:text-xs" style={{ color: 'var(--muted)' }}>Presenca Baixa</span>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{byTipo['presenca_baixa'] || 0}</p>
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Clock size={14} style={{ color: '#F59E0B' }} />
                        <span className="text-[10px] sm:text-xs" style={{ color: 'var(--muted)' }}>Atrasos</span>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{(byTipo['atrasos'] || 0) + (byTipo['atraso'] || 0)}</p>
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Building2 size={14} style={{ color: '#3B82F6' }} />
                        <span className="text-[10px] sm:text-xs" style={{ color: 'var(--muted)' }}>Eventos de Ponto</span>
                    </div>
                    <p className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{bySource['ponto_event'] || 0}</p>
                </div>
            </div>

            {/* Alert List */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-gold-primary)' }} /></div>
            ) : alerts.length === 0 ? (
                <div className="rounded-xl p-8 text-center" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <Bell size={32} className="mx-auto mb-3" style={{ color: 'var(--muted-2)' }} />
                    <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Nenhum alerta encontrado</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                        {hasActiveFilters ? 'Tente alterar os filtros aplicados.' : 'Os alertas aparecerao aqui conforme forem gerados.'}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a)).map(([date, items]) => (
                        <div key={date}>
                            <div className="flex items-center gap-2 mb-2">
                                <Calendar size={12} style={{ color: 'var(--sv-gold-primary)' }} />
                                <span className="text-xs font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>{date}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', color: 'var(--sv-gold-primary)' }}>{items.length}</span>
                            </div>
                            <div className="space-y-1.5">
                                {items.map((a, i) => {
                                    const colors = TIPO_COLORS[a.tipo] || TIPO_COLORS.info;
                                    const time = (a.timestamp || '').split('T')[1]?.slice(0, 5) || '';
                                    return (
                                        <div key={a.alert_id || i} className="flex items-center gap-3 p-3 rounded-lg transition-all hover:scale-[1.005]" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`alert-item-${a.alert_id || i}`}>
                                            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: colors.bg }}>
                                                {a.tipo === 'presenca_baixa' ? <AlertTriangle size={14} style={{ color: colors.text }} /> :
                                                 a.tipo === 'atrasos' || a.tipo === 'atraso' ? <Clock size={14} style={{ color: colors.text }} /> :
                                                 a.tipo === 'ajustes_pendentes' || a.tipo === 'ajuste' ? <ClipboardList size={14} style={{ color: colors.text }} /> :
                                                 a.tipo === 'entrada' ? <CheckCircle size={14} style={{ color: colors.text }} /> :
                                                 a.tipo === 'saida' ? <XCircle size={14} style={{ color: colors.text }} /> :
                                                 <Bell size={14} style={{ color: colors.text }} />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: colors.bg, color: colors.text }}>{TIPO_LABELS[a.tipo] || a.tipo}</span>
                                                    {a.unidade_name && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(59,130,246,0.08)', color: '#3B82F6' }}>{a.unidade_name}</span>}
                                                    {a.user_name && <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{a.user_name}</span>}
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(107,114,128,0.06)', color: '#6B7280' }}>{SOURCE_LABELS[a.source] || a.source}</span>
                                                </div>
                                                <p className="text-xs mt-1 truncate" style={{ color: 'var(--text)' }}>{a.message || `${a.user_name || ''} - ${TIPO_LABELS[a.tipo] || a.tipo}`}</p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <span className="text-[10px] font-mono" style={{ color: 'var(--muted)' }}>{time}</span>
                                                <div className="mt-0.5">
                                                    <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: a.status === 'sent' ? 'rgba(16,185,129,0.08)' : a.status === 'test' ? 'rgba(245,158,11,0.08)' : 'rgba(107,114,128,0.08)', color: a.status === 'sent' ? '#10B981' : a.status === 'test' ? '#F59E0B' : '#6B7280' }}>
                                                        {a.status === 'sent' ? 'Enviado' : a.status === 'test' ? 'Teste' : a.status || '?'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
